import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Divider, FormHelperText, Grid, ListItemText, MenuItem, Select, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Duration_Type_Enum, Preservation_Method_Enum, Product_Label_Name_Enum, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { PackagingFamilyTypeIntl } from '../../../common/i18n/product-family.translation';
import { logoLabels } from '../../../packaging/packaging-formatter';
import { RoutePaths } from '../../../routes/AppRoutes';
import { DraftProduct, draftProductAtom, productSubmittedAtom } from '../state';
import InvalidFormBanner from '../InvalidFormBanner';
import useOpenable from '../../../hooks/useOpenable';
import useSortedProductFamiliesMap from '../../../hooks/useSortedProductFamiliesMap';
import ProductFormQuitBtn from '../ProductFormQuitBtn';
import ProductFormSpecificationsFields from '../specifications/ProductFormSpecificationsFields';
import { currentContextAtom } from '../../../state';
import ProductUtils from '../../common/products.utils';

export default function ProductFormInfo(): React.JSX.Element {
  const [draftProduct, setDraftProduct] = useAtom(draftProductAtom);
  const newProductDefaultValues = {
    name: '',
    sub_family: '' as Product_Sub_Family_Name_Enum,
    labels: [] as Product_Label_Name_Enum[],
    vat_rate: 0,
    ingredients: '',
    shelf_life_duration_type: Duration_Type_Enum.Days,
    allergens: '',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    calories: '' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    calories_kj: '' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fat: '' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    saturated_fat: '' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    carbohydrate: '' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sugars: '' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sodium: '' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protein: '' as any,
    description: '',
    brand: '',
    alcohol_by_volume: null,
    is_exempt_from_ingredient_list: null,
    has_no_allergen_declaration: null,
    preservation_method: null,
  };
  const form = useForm<DraftProduct>({
    defaultValues: draftProduct || newProductDefaultValues,
  });
  const {
    control,
    handleSubmit,
    formState,
    watch,
    setValue,
    reset,
  } = form;
  const watchLabels = watch('labels', []);
  const watchSubFamily = watch('sub_family');
  const { t } = useTranslation(['common', 'products', 'product-family', 'validation']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    isOpen: isInvalidFormBannerOpen,
    open: openInvalidFormBanner,
    close: closeInvalidFormBanner,
  } = useOpenable();
  const setProductSubmitted = useSetAtom(productSubmittedAtom);
  const productFamiliesMap = useSortedProductFamiliesMap(Object.keys(PackagingFamilyTypeIntl) as Product_Sub_Family_Name_Enum[]);
  const productId = searchParams.get('productId');
  const [marketingHighlightsExpanded, setMarketingHighlightsExpanded] = useState(false);
  const { companyName } = useAtomValue(currentContextAtom);

  const shouldBlock = useMemo((): boolean => {
    return formState.isDirty && !!Object.keys(formState.dirtyFields).length && !formState.isSubmitSuccessful;
  }, [formState]);

  const onSubmit = (data: DraftProduct): void => {
    setDraftProduct(data);

    if (productId) {
      setProductSubmitted(true);
      reset(undefined, { keepValues: true });
    }
  };

  useEffect(() => {
    if (formState.isSubmitSuccessful && !productId) {
      navigate({
        pathname: RoutePaths.PRODUCT_ADD_SPECIFICATIONS,
        search: `?${searchParams.toString()}`,
      });
    } else if (formState.submitCount && Object.keys(formState.errors).length) {
      openInvalidFormBanner();
    }
  }, [formState]);

  useEffect(() => {
    if (!productId && companyName && !draftProduct?.brand) {
      setValue('brand', companyName, { shouldDirty: false });
    }
  }, [companyName]);

  const familyValues = useMemo(() => {
    return productFamiliesMap
      .map(({ value, label }) =>
        <MenuItem value={value} key={value}>{label}</MenuItem>);
  }, [PackagingFamilyTypeIntl]);

  const labelValues = useMemo(() => {
    return (Object.keys(logoLabels) as Product_Label_Name_Enum[])
      .map((label) => ({ ...logoLabels[label], label }))
      .sort((a, b) => a.alt.localeCompare(b.alt))
      .map(({ label, alt }) => (
        <MenuItem value={label} key={label}>
          <ListItemText primary={alt} />
        </MenuItem>
      ));
  }, [watchLabels]);

  const isAlcoholFamily = useMemo(() => {
    return ProductUtils.isAlcoholProduct(watchSubFamily);
  }, [watchSubFamily]);

  const handleSubFamilyChange = (subFamily: Product_Sub_Family_Name_Enum): void => {
    if (subFamily === Product_Sub_Family_Name_Enum.RawMeat) {
      setValue('preservation_method', Preservation_Method_Enum.Refrigerated);
    } else if (subFamily === Product_Sub_Family_Name_Enum.FrozenFood) {
      setValue('preservation_method', Preservation_Method_Enum.Frozen);
    } else {
      setValue('preservation_method', null);
    }
  };

  const removeLabels = useCallback((value: Product_Label_Name_Enum): void => {
    const newLabels = [...watchLabels];
    const foundIndex = newLabels.indexOf(value);

    newLabels.splice(foundIndex, 1);
    setValue('labels', newLabels, { shouldValidate: true, shouldDirty: true });
  }, [watchLabels]);

  const renderLabelValues = (selected: Product_Label_Name_Enum[]): React.JSX.Element => {
    if (!selected.length) {
      return <Typography sx={{color:'placeholder.main'}}>{t('products:certifications_and_qualities_placeholder')}</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => (
          <Chip
            key={value}
            label={logoLabels[value].alt}
            size="small"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onDelete={() => removeLabels(value)}
          />
        ))}
      </Box>
    );
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container rowSpacing={4}>
          {!!productId && (
            <Typography variant="titleLarge">
              {t('products:identification')}
            </Typography>
          )}
          <Grid size={{ xs: 12 }} container spacing={2}>
            <Grid
              size={{ xs: 12, sm: 'grow' }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="titleMedium">
                {t('products:product_title')}*
              </Typography>
              <Typography variant="bodySmall">
                {t('products:product_title_description')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 5 }}>
              <Controller
                name="name"
                control={control}
                rules={{ required: t('validation:this_field_is_required') as string }}
                render={({ field, fieldState }) => (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      autoComplete="off"
                      name={field.name}
                      value={field.value}
                      inputRef={field.ref}
                      onChange={field.onChange}
                      placeholder={t('products:product_title_placeholder')}
                      error={!!fieldState.error}
                    />
                    {fieldState.error
                      && <FormHelperText sx={{ ml: 2 }} error>{fieldState.error?.message}</FormHelperText>}
                  </>
                )}
              />
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} container spacing={2}>
            <Grid
              size={{ xs: 12, sm: 'grow' }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="titleMedium">
                {t('products:family')}*
              </Typography>
              <Typography variant="bodySmall">
                {t('products:family_description')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 5 }}>
              <Controller
                name="sub_family"
                control={control}
                rules={{ required: t('validation:this_field_is_required') as string }}
                render={({ field, fieldState }) => (
                  <>
                    <Select
                      size="small"
                      fullWidth
                      variant="outlined"
                      name={field.name}
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event);
                        handleSubFamilyChange(event.target.value);
                      }}
                      displayEmpty
                      renderValue={(selected) => (
                        !selected
                          ? <Typography sx={{color:'placeholder.main'}}>{t('products:family_placeholder')}</Typography>
                          : t(`product-family:${PackagingFamilyTypeIntl[selected as Product_Sub_Family_Name_Enum]}`)
                      )}
                      error={!!fieldState.error}
                    >
                      {familyValues}
                    </Select>
                    {!!fieldState.error
                      && <FormHelperText sx={{ ml: 2 }} error>{fieldState.error?.message}</FormHelperText>}
                  </>
                )}
              />
            </Grid>
          </Grid>

          {isAlcoholFamily && (
            <Grid size={{ xs: 12 }} container spacing={2}>
              <Grid
                size={{ xs: 12, sm: 'grow' }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="titleMedium">
                  {t('products:alcohol_by_volume')}
                </Typography>
                <Typography variant="bodySmall">
                  {t('products:alcohol_by_volume_description')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, lg: 5 }}>
                <Controller
                  name="alcohol_by_volume"
                  control={control}
                  rules={{
                    validate: (value) => !value || (value >= 0 && value <= 100),
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        variant="outlined"
                        autoComplete="off"
                        name={field.name}
                        value={field.value}
                        inputRef={field.ref}
                        onChange={field.onChange}
                        placeholder={t('products:alcohol_by_volume_placeholder')}
                        error={!!fieldState.error}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Typography
                                variant="bodyMedium"
                                color="onSurfaceVariant"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {t('products:alcohol_by_volume_unit')}
                              </Typography>
                            ),
                          },
                        }}
                      />
                      {fieldState.error
                        && <FormHelperText sx={{ ml: 2 }} error>{t('validation:number_must_be_between_0_and_100')}</FormHelperText>}
                    </>
                  )}
                />
              </Grid>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <Accordion
              expanded={marketingHighlightsExpanded}
              onChange={(event, expanded) => setMarketingHighlightsExpanded(expanded)}
              disableGutters
              sx={{
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'outlineVariant.main',
                borderRadius: '8px !important',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  py: 0.5,
                }}
              >
                <Typography variant="titleSmall">
                  {t('products:marketing_highlights')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'outlineVariant.main',
                  px: 2,
                  pb: 2,
                  pt: 2,
                }}
              >
                <Grid container rowSpacing={4}>
                  <Grid size={{ xs: 12 }} container spacing={2}>
                    <Grid
                      size={{ xs: 12, sm: 'grow' }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="titleMedium">
                        {t('products:brand')}
                      </Typography>
                      <Typography variant="bodySmall">
                        {t('products:brand_description')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 5 }}>
                      <Controller
                        name="brand"
                        control={control}
                        render={({ field, fieldState }) => (
                          <>
                            <TextField
                              size="small"
                              fullWidth
                              variant="outlined"
                              autoComplete="off"
                              name={field.name}
                              value={field.value}
                              inputRef={field.ref}
                              onChange={field.onChange}
                              placeholder={t('products:brand_placeholder')}
                              error={!!fieldState.error}
                            />
                            {fieldState.error
                              && <FormHelperText sx={{ ml: 2 }} error>{fieldState.error?.message}</FormHelperText>}
                          </>
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Typography variant="titleMedium" sx={{ mb: 0.5 }}>
                      {t('products:product_description')}
                    </Typography>
                    <Typography variant="bodySmall" sx={{ mb: 2 }}>
                      {t('products:product_description_details')}
                    </Typography>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <TextField
                            fullWidth
                            variant="outlined"
                            slotProps={{
                              htmlInput: {
                                style: {
                                  minHeight: 80,
                                },
                              },
                            }}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t('products:add_description')}
                            multiline
                            error={!!fieldState.error}
                          />
                          {fieldState.error
                            && <FormHelperText sx={{ ml: 2 }} error>{fieldState.error?.message}</FormHelperText>}
                        </>
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }} container spacing={2}>
                    <Grid
                      size={{ xs: 12, sm: 'grow' }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="titleMedium">
                        {t('products:certifications_and_qualities')}
                      </Typography>
                      <Typography variant="bodySmall">
                        {t('products:certifications_and_qualities_description')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 5 }}>
                      <Controller
                        name="labels"
                        control={control}
                        render={({ field }) => (
                          <Select
                            size="small"
                            fullWidth
                            variant="outlined"
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            multiple
                            displayEmpty
                            renderValue={renderLabelValues}
                          >
                            {labelValues}
                          </Select>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                backgroundColor: 'tertiaryContainer.main',
                px: 3,
                py: 2,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <span>💡</span>
              <Typography variant="bodySmall" color="onTertiaryContainer">
                {t('products:tip_descriptive_title')}
              </Typography>
            </Box>
          </Grid>
          <Divider sx={{ width: '100%' }} />
          {!!productId && (
            <>
              <Typography variant="titleLarge">
                {t('products:specifications')}
              </Typography>
              <ProductFormSpecificationsFields control={control} />
            </>
          )}

          {isInvalidFormBannerOpen && (
            <Grid
              size={{ xs: 12 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <InvalidFormBanner close={closeInvalidFormBanner} message={t('validation:fill_the_marked_field_to_continue')} />
            </Grid>
          )}
          <Grid
            size={{ xs: 12 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: 'center',
              gap: { xs: 1, sm: 2 },
            }}
          >
            <ProductFormQuitBtn productId={productId || undefined} shouldBlock={shouldBlock} />
            <Button
              variant="contained"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              type="submit"
              disabled={!!productId && !formState.isDirty}
            >
              {productId ? t('common:validate') : t('common:next')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}
